/** Abrechnung-Detail (Screen laden/Abrechnung-Detail): Berechnung, verkaufte Artikel, Barauszahlung mit Unterschrift, „Als überwiesen markieren“, senden, PDF. */
import { useState } from "react";
import { Link, useParams } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge, TextField, Total } from "../ui";
import { Card, Dl, PageHeader, Split } from "../ui/Layout";
import { SignaturePad } from "../ui/SignaturePad";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatDatumZeit, formatTag } from "@shared/zeit";
import { monatsName } from "@shared/abrechnung";
import { ABRECHNUNG_STATUS, abrechnungPdfUrl, quittungPdfUrl, useAbrechnung, useAbrechnungAktion } from "./geld-api";

export function AbrechnungDetail() {
  const { id = "" } = useParams();
  const q = useAbrechnung(id);
  const aktion = useAbrechnungAktion();
  const [betrag, setBetrag] = useState("");
  const [unterschrift, setUnterschrift] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  return (
    <Seitenzustand query={q} lines={8}>
      {(d) => {
        const a = d.abrechnung;
        const st = ABRECHNUNG_STATUS[a.status]!;
        const summe = a.umsatzCent + a.rabatteCent + a.rueckgabenCent + a.provisionCent + a.mieteCent + a.uebertragCent + a.sonstigesCent;
        const offen = ["erstellt", "versendet"].includes(a.status) && a.auszahlungCent > 0;
        const betragCent = parseEuro(betrag || (a.auszahlungCent / 100).toFixed(2).replace(".", ",")) ?? a.auszahlungCent;
        const fehler = (e: unknown, s: string) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : s });
        return (
          <>
            <PageHeader
              kicker={<Link to="/laden/abrechnungen">Abrechnungen</Link>}
              title={`${a.art === "schluss" ? "Schlussabrechnung" : "Abrechnung"} ${monatsName(a.periodeVon)}`}
              sub={<span className="sc-toolbar"><StatusBadge status={st.badge}>{st.text}</StatusBadge><span><Link to={`/laden/mieter/${d.mieter.id}`}>{d.mieter.name}</Link> · Nr. {d.mieter.mieternummer} · {a.nummer} · {formatTag(a.periodeVon)}–{formatTag(a.periodeBis)}</span></span>}
              actions={
                <>
                  <a href={abrechnungPdfUrl(a.id)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary">PDF</a>
                  <Button variant="secondary" icon="mail" loading={aktion.isPending} disabled={!d.mieter.email || a.status === "ausgezahlt"} title={d.mieter.email ? undefined : "Mieter hat keine E-Mail"} onClick={() => aktion.mutate({ id: a.id, aktion: "senden" }, { onSuccess: () => setMeldung({ tone: "success", text: "An den Mieter gesendet (E-Mail mit Link ins Portal)." }), onError: (e) => fehler(e, "Senden hat nicht geklappt.") })}>
                    An Mieter senden
                  </Button>
                </>
              }
            />
            {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
            <Split
              main={
                <>
                  <Card title="Berechnung">
                    <Dl
                      rows={[
                        [`Verkäufe, ${d.verkaeufe.length} Artikel`, formatEuro(a.umsatzCent)],
                        ...(a.rabatteCent ? [["Rabatte", formatEuro(a.rabatteCent)] as [string, string]] : []),
                        ...(a.rueckgabenCent ? [[`Rückgaben (${d.rueckgaben.length})`, formatEuro(a.rueckgabenCent)] as [string, string]] : []),
                        ["Provision", formatEuro(a.provisionCent)],
                        ...d.mietrechnungen.map((r) => [`Miete ${r.nummer} · ${formatTag(r.leistungVon)}–${formatTag(r.leistungBis)}`, formatEuro(-r.bruttoCent)] as [string, string]),
                        ...(a.uebertragCent ? [["Übertrag aus Vorperiode", formatEuro(a.uebertragCent)] as [string, string]] : []),
                        ...(a.sonstigesCent ? [["Kaution / Sonstiges", formatEuro(a.sonstigesCent)] as [string, string]] : []),
                      ]}
                    />
                    <Total amountCent={Math.abs(summe)} label={a.status === "forderung" ? "Forderung des Ladens" : a.status === "uebertragen" ? "Übertrag in die nächste Periode" : "Auszahlung"} size="lg" caption={a.status === "forderung" ? "Wird mit der nächsten Abrechnung verrechnet" : a.status === "uebertragen" ? "Unter dem Mindestbetrag" : d.mieter.auszahlungsweg === "bar" ? "Bar im Laden" : `Per Überweisung${d.mieter.ibanMaske ? ` auf ${d.mieter.ibanMaske}` : " – keine IBAN hinterlegt"}`} />
                  </Card>
                  <Card title={`Verkaufte Artikel · ${d.verkaeufe.length}`} flush>
                    <div className="app-tabellen-scroll">
                      <table className="rg-table">
                        <thead><tr><th>Datum</th><th>Code</th><th>Artikel</th><th>Zahlung</th><th className="is-num">Preis</th><th className="is-num">Rabatt</th><th className="is-num">Erlös</th></tr></thead>
                        <tbody>
                          {d.verkaeufe.length === 0 ? <tr><td colSpan={7} className="rg-table__empty">Keine Verkäufe in dieser Periode.</td></tr> : null}
                          {d.verkaeufe.map((v) => (
                            <tr key={v.id}><td>{v.bezahltAt ? formatTag(v.bezahltAt.slice(0, 10)) : "–"}</td><td className="app-code">{v.code ?? "–"}</td><td>{v.bezeichnung}{v.rabattGrund ? <span className="sc-muted sc-small"> · Rabatt: {v.rabattGrund}</span> : null}</td><td>{v.zahlungsart === "bar" ? "Bar" : v.zahlungsart === "karte" ? "Karte" : "–"}</td><td className="is-num">{formatEuro(v.preisCent)}</td><td className="is-num">{v.rabattCent ? formatEuro(-v.rabattCent) : "–"}</td><td className="is-num">{formatEuro(v.endpreisCent)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  {d.rueckgaben.length ? (
                    <Card title="Rückgaben" flush>
                      <table className="rg-table"><tbody>{d.rueckgaben.map((r) => <tr key={r.id}><td>{formatTag(r.erstelltAt.slice(0, 10))}</td><td className="app-code">{r.code}</td><td>{r.bezeichnung}</td><td>{r.grund}</td><td className="is-num">{formatEuro(-r.betragCent)}</td></tr>)}</tbody></table>
                    </Card>
                  ) : null}
                  {offen ? (
                    <Card title="Barauszahlung erfassen">
                      <p className="sc-muted">Nur wenn {d.mieter.name.split(" ")[0]} das Geld im Laden abholt. Die Quittung mit Unterschrift wird gespeichert und als PDF abrufbar.</p>
                      <div className="sc-form">
                        <TextField label="Betrag" value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" suffix="€" placeholder={(a.auszahlungCent / 100).toFixed(2).replace(".", ",")} hint={`Höchstens ${formatEuro(a.auszahlungCent)} · ein Rest bleibt als Übertrag`} />
                      </div>
                      <span className="rg-field__label">Unterschrift Empfänger</span>
                      <SignaturePad onChange={setUnterschrift} />
                      <div className="app-form__actions">
                        <Button variant="success" icon="banknote" loading={aktion.isPending} disabled={!unterschrift || betragCent <= 0 || betragCent > a.auszahlungCent} onClick={() => aktion.mutate({ id: a.id, aktion: "auszahlung", daten: { art: "bar", betragCent, unterschriftPng: unterschrift ?? undefined } }, { onSuccess: () => setMeldung({ tone: "success", text: "Barauszahlung erfasst – Kassenbuch und Quittung sind geschrieben." }), onError: (e) => fehler(e, "Auszahlung hat nicht geklappt.") })}>
                          Barauszahlung bestätigen
                        </Button>
                      </div>
                    </Card>
                  ) : null}
                </>
              }
              aside={
                <>
                  <Card title="Auszahlen">
                    {offen ? (
                      <div className="sc-col">
                        <Button variant="primary" icon="check" block loading={aktion.isPending} onClick={() => confirm(`${formatEuro(a.auszahlungCent)} als überwiesen markieren?`) && aktion.mutate({ id: a.id, aktion: "auszahlung", daten: { art: "ueberweisung" } }, { onSuccess: () => setMeldung({ tone: "success", text: "Als überwiesen markiert." }), onError: (e) => fehler(e, "Das hat nicht geklappt.") })}>
                          Als überwiesen markieren
                        </Button>
                        <p className="sc-muted sc-small">{d.mieter.ibanMaske ? `${d.mieter.kontoinhaber || d.mieter.name} · ${d.mieter.ibanMaske}` : "Keine IBAN hinterlegt – bar auszahlen oder IBAN im Mieter-Detail eintragen."} SEPA-Sammeldatei kommt in V2.</p>
                      </div>
                    ) : null}
                    <div className="sc-col" style={{ marginTop: offen ? "var(--space-3)" : 0 }}>
                      <span>✓ Erstellt {formatDatumZeit(a.erstelltAt)}</span>
                      <span>{a.versendetAt ? `✓ Gesendet ${formatDatumZeit(a.versendetAt)}` : "○ Noch nicht gesendet"}</span>
                      <span>{d.auszahlung ? `✓ ${d.auszahlung.art === "bar" ? "Bar ausgezahlt" : "Überwiesen"} ${formatDatumZeit(d.auszahlung.ausgezahltAt)} · ${formatEuro(d.auszahlung.betragCent)}` : a.status === "forderung" || a.status === "uebertragen" ? "– Keine Auszahlung (Übertrag/Forderung)" : "○ Auszahlung offen"}</span>
                      {d.auszahlung?.quittungKey ? <a href={quittungPdfUrl(d.auszahlung.id)} target="_blank" rel="noreferrer">Quittung (PDF)</a> : null}
                    </div>
                  </Card>
                  <Card title="Buchungen im Mieterkonto" flush>
                    <table className="rg-table"><tbody>{d.buchungen.map((b) => <tr key={b.id}><td>{formatTag(b.buchungsdatum)}</td><td>{b.art}</td><td className="is-num">{formatEuro(b.betragCent)}</td></tr>)}</tbody></table>
                  </Card>
                </>
              }
            />
          </>
        );
      }}
    </Seitenzustand>
  );
}
